<template>
  <div class="audio-toggle-fixed">
    <!-- 簡約音訊按鈕 -->
    <button 
      @click="toggleMute" 
      class="audio-btn"
      :class="{ 'muted': isMuted }"
      :aria-label="isMuted ? '開啟背景音樂' : '關閉背景音樂'"
      :title="isMuted ? '開啟背景音樂' : '關閉背景音樂'"
    >
      <!-- 音訊圖示 - 使用 SVG 以獲得更好的控制 -->
      <svg v-if="!isMuted" class="audio-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
        <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
      </svg>
      <svg v-else class="audio-icon muted" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
        <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
      </svg>
      
      <!-- 動態音波效果 -->
      <div class="audio-waves" v-if="!isMuted">
        <span class="wave"></span>
        <span class="wave"></span>
        <span class="wave"></span>
      </div>
    </button>
    
    <!-- 隱藏的 audio 元素 -->
    <audio 
      ref="audio"
      :src="audioSrc"
      loop
    ></audio>
  </div>
</template>

<script>
export default {
  name: 'AudioPlayer',
  data() {
    return {
      audioSrc: '/BGM.mp3',
      isMuted: false
    }
  },
  mounted() {
    // 從 localStorage 讀取用戶靜音偏好
    const savedMuted = localStorage.getItem('bgm-muted');
    if (savedMuted !== null) {
      this.isMuted = savedMuted === 'true';
    }
    
    // 設置固定音量為 10%
    this.$refs.audio.volume = 0.1;
    this.$refs.audio.muted = this.isMuted;
    
    // 嘗試自動播放
    this.$nextTick(() => {
      const playPromise = this.$refs.audio.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            console.log('背景音樂已開始播放');
          })
          .catch(error => {
            console.log('自動播放被阻止:', error);
            // 靜默處理，等待用戶互動後再播放
            this.isMuted = true;
          });
      }
    });
  },
  methods: {
    toggleMute() {
      this.isMuted = !this.isMuted;
      this.$refs.audio.muted = this.isMuted;
      
      // 如果取消靜音，嘗試播放
      if (!this.isMuted) {
        const playPromise = this.$refs.audio.play();
        if (playPromise !== undefined) {
          playPromise.catch(error => {
            console.log('播放失敗:', error);
          });
        }
      }
      
      // 儲存用戶偏好
      localStorage.setItem('bgm-muted', this.isMuted);
    }
  },
  beforeUnmount() {
    // 清理
    if (this.$refs.audio) {
      this.$refs.audio.pause();
    }
  }
}
</script>

<style scoped>
/* 固定在右下角的音訊按鈕 */
.audio-toggle-fixed {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 9999;
}

/* 音訊按鈕 */
.audio-btn {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: rgba(0, 123, 255, 0.9);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: visible;
}

.audio-btn:hover {
  background: rgba(0, 123, 255, 1);
  box-shadow: 0 6px 20px rgba(0, 123, 255, 0.4);
  transform: scale(1.05);
}

.audio-btn:active {
  transform: scale(0.95);
}

/* 靜音狀態 */
.audio-btn.muted {
  background: rgba(108, 117, 125, 0.9);
  box-shadow: 0 4px 12px rgba(108, 117, 125, 0.3);
}

.audio-btn.muted:hover {
  background: rgba(108, 117, 125, 1);
  box-shadow: 0 6px 20px rgba(108, 117, 125, 0.4);
}

/* 音訊圖示 */
.audio-icon {
  width: 24px;
  height: 24px;
  color: white;
  transition: all 0.3s ease;
}

/* 動態音波效果 */
.audio-waves {
  position: absolute;
  right: -8px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  gap: 2px;
  align-items: center;
}

.wave {
  width: 2px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 2px;
  animation: wave-animation 1.2s ease-in-out infinite;
}

.wave:nth-child(1) {
  height: 8px;
  animation-delay: 0s;
}

.wave:nth-child(2) {
  height: 12px;
  animation-delay: 0.2s;
}

.wave:nth-child(3) {
  height: 8px;
  animation-delay: 0.4s;
}

@keyframes wave-animation {
  0%, 100% {
    transform: scaleY(0.5);
    opacity: 0.6;
  }
  50% {
    transform: scaleY(1);
    opacity: 1;
  }
}

/* 脈衝動畫（當音樂播放時） */
.audio-btn:not(.muted)::before {
  content: '';
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: rgba(0, 123, 255, 0.3);
  animation: pulse 2s ease-out infinite;
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 0.8;
  }
  100% {
    transform: scale(1.5);
    opacity: 0;
  }
}

/* 行動裝置優化 */
@media (max-width: 768px) {
  .audio-toggle-fixed {
    bottom: 20px;  /* 對齊右下角 */
    right: 20px;
  }
  
  .audio-btn {
    width: 45px;
    height: 45px;
  }
  
  .audio-icon {
    width: 20px;
    height: 20px;
  }
  
  .audio-waves {
    right: -6px;
  }
  
  .wave {
    width: 1.5px;
  }
  
  .wave:nth-child(1) {
    height: 6px;
  }
  
  .wave:nth-child(2) {
    height: 10px;
  }
  
  .wave:nth-child(3) {
    height: 6px;
  }
}

/* 超小螢幕 */
@media (max-width: 480px) {
  .audio-toggle-fixed {
    bottom: 15px;  /* 對齊右下角 */
    right: 15px;
  }
  
  .audio-btn {
    width: 42px;
    height: 42px;
  }
}
</style>


